/**
 * Что уходит на сервер из теста с таймером (`quiz_with_timer`).
 *
 * ⚠️ ЗАЧЕМ. До 21.09.2026 таймер блока шёл с появления блока на экране, а по
 * истечении каждый неотвеченный вопрос «отвечался» пустым: на сервер уходило
 * `["","","","",""]`, ученик получал «Неверно» за ответы, которых не давал, и
 * раздел закрывался сам (`quiz_with_timer` входит в `ANSWERABLE_TYPES`,
 * `sectionCompletion.ts`). Отсюда жалобы «захожу в урок — всё само выполнено».
 * На проде за три недели так записалось 628 ответов из 1328 по этому типу —
 * 47 %, у 574 учеников; у всех остальных типов заданий таких записей ноль.
 * Разбор — `docs/CHECK-2026-09-21-QUIZ-EMPTY-ANSWERS.md`, договор —
 * `docs/CONTRACTS/quiz-timer.md`.
 *
 * ⚠️ ПРАВИЛО ВЛАДЕЛЬЦА 21.09.2026. Ни одного выбора — ответ НЕ отправляется
 * вовсе (блок показывает «время вышло», раздел остаётся непройденным). Выбран
 * хоть один вариант — отправляется как есть: труд ученика не пропадает, а
 * неотвеченные вопросы остаются пустыми местами в массиве.
 *
 * ⚠️ ФОРМА ОТВЕТА НЕ МЕНЯЕТСЯ. Сервер (`progress-service`, `field_grader.go`)
 * ждёт для одного вопроса строку («2»), для нескольких — JSON-массив строк по
 * порядку вопросов. Пустое место — пустая строка, порядок сохраняется.
 *
 * Логика живёт здесь, а не в `blockRenderers.tsx`, потому что проверки фронта
 * собираются только из `src/**\/*.test.ts` (`vitest.config.ts`): внутри `.tsx`
 * это правило не проверялось бы вовсе — та же причина, что у
 * `sectionCompletion.ts` и `quizCorrectGuard.ts`.
 */

/** Настоящий выбор ученика: не `null` (не отвечал) и не пустая строка. */
export const isPick = (v: string | null | undefined): boolean => v != null && v !== '';

/** Есть ли хоть один настоящий выбор среди вопросов блока. */
export const hasAnyPick = (data: readonly (string | null)[]): boolean => data.some(isPick);

export type QuizAnswer = {
  /** Тело ответа для сервера; пусто, когда отправлять нечего. */
  answer_data: string;
  /** Отправлять ли ответ вообще. */
  send: boolean;
};

/**
 * Собирает ответ блока из выборов по вопросам.
 *
 * `single` — блок из одного вопроса: тело ответа остаётся «сырым» (строка
 * индекса или JSON множественного выбора), как ждёт серверная проверка.
 */
export function buildQuizAnswer(data: readonly (string | null)[], single: boolean): QuizAnswer {
  if (!hasAnyPick(data)) return { answer_data: '', send: false };
  if (single) return { answer_data: data[0] ?? '', send: true };
  return { answer_data: JSON.stringify(data.map((v) => v ?? '')), send: true };
}
